import { useEffect, useState } from 'react';
import { pdApi } from '../api';
import '../screens.css';
import './Dashboards.css';
import { HelpPanel } from '../HelpPanel';
import { OPPORTUNITIES_DASHBOARD, PROJECTS_DASHBOARD } from '../helpContent';

/**
 * Projects → Dashboard, and Opportunities → Dashboard.
 *
 * Cloned from `views/projects-dashboard.ejs` and `views/opportunities-dashboard.ejs`.
 *
 * The source renders these with Chart.js. The measures, groupings and orderings here are
 * the source's exactly; the presentation is CSS bars rather than a canvas, because pulling
 * a charting library in for two screens is a dependency this platform does not otherwise
 * carry. Logged in docs/DEVIATIONS.md as a presentation change — no number differs.
 *
 * THE PROJECT PROGRESS COUNTS ARE CUMULATIVE, not exclusive: an opportunity that is
 * Delivered is counted in delivered, in-development, scored AND approved, because it has
 * passed through all of them. Read as a funnel, each bar is "reached at least this far" —
 * which is why they never sum to the total.
 */

type Dict = Record<string, unknown>;

const money = (v: unknown) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="dash-tile">
      <div className="dash-tile-value">{value}</div>
      <div className="dash-tile-label">{label}</div>
      {hint ? <div className="dash-tile-hint">{hint}</div> : null}
    </div>
  );
}

/** A labelled proportional bar. Widths are relative to the largest value in the set. */
function Bar({ label, value, max, format }: { label: string; value: number; max: number; format?: (n: number) => string }) {
  return (
    <div className="dash-bar-row">
      <div className="dash-bar-label" title={label}>
        {label}
      </div>
      <div className="dash-bar-track">
        <div className="dash-bar-fill" style={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }} />
      </div>
      <div className="dash-bar-value">{format ? format(value) : value}</div>
    </div>
  );
}

export function ProjectsDashboard() {
  const [data, setData] = useState<Dict | null>(null);
  const [progress, setProgress] = useState<Dict[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setData(await pdApi.projects.dashboard());
        setProgress((await pdApi.projects.progressStats()).data);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const chart = (data?.chart_data as Dict[]) ?? [];
  const maxOpps = Math.max(1, ...chart.map((c) => Number(c.opportunities ?? 0)));
  const maxBenefit = Math.max(1, ...chart.map((c) => Number(c.benefit ?? 0)));

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Projects Dashboard</h1>
          <p className="pd-page-header__subtitle">
            Portfolio totals, and how far each project has carried its opportunities.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={PROJECTS_DASHBOARD} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <>
          <div className="dash-tiles">
            <StatTile label="Projects" value={String(data?.total_projects ?? 0)} />
            <StatTile
              label="Linked opportunities"
              value={String(data?.total_linked_opportunities ?? 0)}
            />
            <StatTile
              label="Unlinked opportunities"
              value={String(data?.total_unlinked_opportunities ?? 0)}
              hint="Not assigned to any project"
            />
          </div>

          {chart.length ? (
            <>
              <h2 className="pd-section-title">Opportunities per project</h2>
              <div className="dash-bars">
                {chart.map((c) => (
                  <Bar
                    key={String(c.id)}
                    label={String(c.name ?? c.id)}
                    value={Number(c.opportunities ?? 0)}
                    max={maxOpps}
                  />
                ))}
              </div>

              <h2 className="pd-section-title">Annual benefit per project</h2>
              <div className="dash-bars">
                {chart.map((c) => (
                  <Bar
                    key={String(c.id)}
                    label={String(c.name ?? c.id)}
                    value={Number(c.benefit ?? 0)}
                    max={maxBenefit}
                    format={money}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="pd-empty">No projects yet.</div>
          )}

          {progress.length ? (
            <>
              <h2 className="pd-section-title">Delivery funnel</h2>
              <p className="pd-hint" style={{ marginBottom: 10 }}>
                Counts are cumulative — an opportunity that is Delivered also counts as
                scored and approved, so the columns do not sum to the total.
              </p>
              <div className="pd-table-wrap">
                <table className="pd-table">
                  <thead>
                    <tr>
                      <th style={{ width: 150 }}>Project</th>
                      <th style={{ width: 100 }}>Total</th>
                      <th style={{ width: 100 }}>Scorable</th>
                      <th style={{ width: 100 }}>Approved</th>
                      <th style={{ width: 100 }}>Scored</th>
                      <th style={{ width: 120 }}>In Development</th>
                      <th style={{ width: 100 }}>Delivered</th>
                      <th style={{ width: 90 }}>Avg score</th>
                      <th style={{ width: 130 }}>Budget used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {progress.map((p) => (
                      <tr key={String(p.project_id)}>
                        <td>{String(p.project_id)}</td>
                        <td className="pd-num">{String(p.total_opportunities ?? 0)}</td>
                        <td className="pd-num">{String(p.relevant_count ?? 0)}</td>
                        <td className="pd-num">{String(p.approved_count ?? 0)}</td>
                        <td className="pd-num">{String(p.scored_count ?? 0)}</td>
                        <td className="pd-num">{String(p.in_development_count ?? 0)}</td>
                        <td className="pd-num">{String(p.delivered_count ?? 0)}</td>
                        <td className="pd-num">
                          {p.avg_score === null || p.avg_score === undefined
                            ? '—'
                            : Number(p.avg_score).toFixed(2)}
                        </td>
                        <td className="pd-num">{money(p.budget_utilized)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

export function OpportunitiesDashboard() {
  const [data, setData] = useState<Dict | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setData(await pdApi.opportunities.dashboard());
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const statuses = (data?.status_distribution as Dict[]) ?? [];
  const l1 = (data?.l1_distribution as Dict[]) ?? [];
  const recent = (data?.recent_opportunities as Dict[]) ?? [];
  const top = (data?.top_scored_opportunities as Dict[]) ?? [];
  const charts = (data?.charts as Dict) ?? {};
  const radar = (charts.portfolioRadar as number[]) ?? [];
  const dims = (charts.dimensions as string[]) ?? [];

  const maxStatus = Math.max(1, ...statuses.map((s) => Number(s.c ?? 0)));
  const maxL1 = Math.max(1, ...l1.map((s) => Number(s.c ?? 0)));

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Scoring Dashboard</h1>
          <p className="pd-page-header__subtitle">
            Where the portfolio stands, and how it profiles across the five assessment
            dimensions.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={OPPORTUNITIES_DASHBOARD} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <>
          <div className="dash-tiles">
            <StatTile label="Opportunities" value={String(data?.total_opportunities ?? 0)} />
            <StatTile label="Scored" value={String(data?.scored_opportunities ?? 0)} />
            <StatTile label="Pending" value={String(data?.pending_opportunities ?? 0)} />
            <StatTile
              label="Average score"
              value={Number(data?.avg_score ?? 0).toFixed(2)}
              hint="Across every scored opportunity"
            />
          </div>

          {radar.length ? (
            <>
              <h2 className="pd-section-title">Portfolio profile (1–5 average)</h2>
              <div className="dash-bars">
                {dims.map((label, i) => (
                  <Bar
                    key={label}
                    label={label}
                    value={radar[i] ?? 0}
                    max={5}
                    format={(n) => n.toFixed(2)}
                  />
                ))}
              </div>
            </>
          ) : null}

          <div className="dash-split">
            <div>
              <h2 className="pd-section-title">By status</h2>
              <div className="dash-bars">
                {statuses.map((s) => (
                  <Bar
                    key={String(s.label)}
                    label={String(s.label)}
                    value={Number(s.c ?? 0)}
                    max={maxStatus}
                  />
                ))}
              </div>
            </div>
            <div>
              <h2 className="pd-section-title">By L1 process</h2>
              <div className="dash-bars">
                {l1.length === 0 ? (
                  <div className="pd-empty">No opportunities are filed under an L1 process.</div>
                ) : (
                  l1.map((s) => (
                    <Bar
                      key={String(s.label)}
                      label={String(s.label)}
                      value={Number(s.c ?? 0)}
                      max={maxL1}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="dash-split">
            <div>
              <h2 className="pd-section-title">Top scored</h2>
              <div className="pd-table-wrap">
                <table className="pd-table">
                  <thead>
                    <tr>
                      <th style={{ width: 160 }}>Opportunity</th>
                      <th style={{ width: 80 }}>Score</th>
                      <th>Recommendation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="pd-empty">
                          Nothing scored yet.
                        </td>
                      </tr>
                    ) : (
                      top.map((o) => (
                        <tr key={String(o.opportunity_id)}>
                          <td>{String(o.opportunity_id)}</td>
                          <td className="pd-num">{Number(o.overall_score).toFixed(2)}</td>
                          <td>
                            <span
                              className="pd-badge"
                              style={{ color: String(o.recommendation_color ?? '#475569') }}
                            >
                              {String(o.recommendation ?? '—')}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h2 className="pd-section-title">Recently added</h2>
              <div className="pd-table-wrap">
                <table className="pd-table">
                  <thead>
                    <tr>
                      <th style={{ width: 160 }}>Opportunity</th>
                      <th>Description</th>
                      <th style={{ width: 130 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="pd-empty">
                          No opportunities yet.
                        </td>
                      </tr>
                    ) : (
                      recent.map((o) => (
                        <tr key={String(o.opportunity_id)}>
                          <td>{String(o.opportunity_id)}</td>
                          <td>{String(o.description)}</td>
                          <td>{String(o.status)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
